import * as React from "react";

import { Resources, useResource } from "Translations/Resources";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";

import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { Authorized } from "Routes/Authorized";
import { BreadcrumbItem } from "Components/Layout/Breadcrumbs";
import { CompaniesSideMenu } from "Components/Companies/CompaniesSideMenu";
import { CompanyDetailAddress } from "Components/Companies/CompanyDetail/CompanyDetailAddress";
import { CompanyDetailConfirmModal } from "Components/Companies/CompanyDetail/CompanyDetailConfirmModal";
import { CompanyDetailRole } from "Components/Companies/CompanyDetail/CompanyDetailRole";
import { CompanyDetailImage } from "Components/Companies/CompanyDetail/CompanyDetailImage";
import { Documents } from "Components/Shared/Documents/Documents";
import { EditIcon } from "Components/Shared/Icons/EditIcon";
import { PrimarySkeleton } from "Components/Shared/PrimarySkeleton";
import { PrimaryTooltip } from "Components/Shared/PrimaryTooltip";
import { RootStateType } from "State/Store";
import { UnstyledLink } from "Components/Shared/Routes/UnstyledLink";
import { getCompaniesDetailAsync } from "State/Companies/CompanyReducer";
import { isNoU } from "Utils/ObjectUtils";
import styled from "styled-components";
import { necessaryAccessRightEditForCompanies } from "Hooks/useHasCurrentUserAccessRight";

type RouteProps = {
  id: string;
};

type Props = {
  companiesListUrl: string;
};

const StyledLayout = styled.div`
  ${props => props.theme.breakpoints.down("xs")} {
    h3 {
      padding: ${props => props.theme.spacing(0, 1)};
    }
  }

  .box-header-buttons {
    display: flex;

    .edit {
      margin-right: 10px;
    }
  }
`;

export const CompanyDetailPage: React.FunctionComponent<Props> = props => {
  const { params } = useRouteMatch<RouteProps>();
  const { t } = useResource();
  const companyID = params.id as any;
  const dispatch = useDispatch();

  const {
    companyDetail: company,
    isLoadingCompanyDetail: isLoading,
  } = useSelector((e: RootStateType) => e.companies);

  const editLink = company
    ? `/${t(Resources.Routes.Companies)}/${t(Resources.Routes.CompanyEdit)}/${
        company.companyID
      }`
    : "";

  React.useEffect(() => {
    if (companyID) {
      dispatch(getCompaniesDetailAsync.request(companyID));
    }
  }, [companyID, dispatch]);

  const getBreadcrumbItems = () => {
    return [
      {
        name: t(Resources.Companies.Title),
        url: t(Resources.Routes.Companies),
      } as BreadcrumbItem,
      {
        name: t(Resources.Companies.Detail.Breadcrumb.Title),
      } as BreadcrumbItem,
    ];
  };

  return (
    <StyledLayout>
      <AuthenticatedLayout
        title={company?.name ?? ""}
        hasSideMenu={!isNoU(companyID)}
        breadcrumbItems={getBreadcrumbItems()}
      >
        <CompaniesSideMenu companyID={companyID} />
        <div className={"container-small"}>
          <div className={"box detail"}>
            <div className={"box-header"}>
              {!isLoading && company ? (
                t(Resources.Companies.Detail.Title)
              ) : (
                <PrimarySkeleton height={30} />
              )}

              <div className={"box-header-buttons"}>
                <Authorized
                  accessRights={necessaryAccessRightEditForCompanies(
                    company?.isCustomer,
                    company?.isSupplier,
                    company?.isMarketplaceEnabled,
                  )}
                >
                  <PrimaryTooltip
                    title={t(Resources.Companies.List.Edit.Tooltip)}
                  >
                    <UnstyledLink
                      to={editLink}
                      className={"bl-btn bl-btn-orange edit"}
                    >
                      <EditIcon />
                    </UnstyledLink>
                  </PrimaryTooltip>
                  <CompanyDetailConfirmModal company={company} />
                </Authorized>
              </div>
            </div>

            <div className={"box-content"}>
              {!isLoading && company ? (
                <>
                  <CompanyDetailAddress company={company} />

                  <CompanyDetailRole company={company} />

                  <CompanyDetailImage
                    imgUrl={company.signaturePhotoUrl}
                    label="Podpis"
                  />

                  <CompanyDetailImage
                    imgUrl={company.logoPhotoUrl}
                    label="Logo"
                  />

                  <Documents
                    documents={company.documents}
                    title={t(Resources.Companies.Detail.Documents.Title)}
                  />
                </>
              ) : (
                <>
                  <PrimarySkeleton height={120} />
                  <PrimarySkeleton height={60} />
                  <PrimarySkeleton height={60} />
                  <PrimarySkeleton height={60} />
                </>
              )}
            </div>
          </div>
        </div>
      </AuthenticatedLayout>
    </StyledLayout>
  );
};
